import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/core';

import { PrimaryButton, SearchField, useFormField } from '@weave/design-system';
import { WeaveTheme } from '@weave/theme-original';

import { selectCurrentLocationId } from 'redux/actions/location';
import { Page } from 'components/shared';
import { useListTagsQuery, useReportEntityTagsStateQuery } from 'apis/wetags';
import { WetagsEntity } from './wetags-entity.component';
import { EntityTag } from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';

const defaultNamespace = 'weave-location';

export const WetagsPage = () => {
  const [editable, setEditable] = useState(false);

  const tagSearchProps = useFormField({
    type: 'text',
    value: '',
  });

  const locationId = useSelector(selectCurrentLocationId);

  const entityTagsQuery = useReportEntityTagsStateQuery({
    namespaceId: defaultNamespace,
    entityId: locationId,
  });

  const tagsQuery = useListTagsQuery({
    namespaceId: defaultNamespace,
  });

  const getTag = (tagId: string | undefined) => {
    if (!tagId) {
      return undefined;
    }
    return tagsQuery.data?.tags?.find((tag) => tag.id === tagId);
  };

  if (entityTagsQuery.isLoading || tagsQuery.isLoading) {
    return <Page title="Tags" loading></Page>;
  } else if (!entityTagsQuery.data || entityTagsQuery.isError) {
    return <Page title="Tags">Error loading tags: {entityTagsQuery.error}</Page>;
  } else {
    const filteredTags = !tagSearchProps.value
      ? entityTagsQuery.data.entityTagStates
      : entityTagsQuery.data.entityTagStates?.filter((et) => {
          return (et.tagId ?? '').includes(tagSearchProps.value);
        });

    // sort tags by name
    filteredTags?.sort((a: EntityTag, b: EntityTag) => {
      return (a?.tagId || '').localeCompare(b?.tagId || '');
    });
    return (
      <Page
        title="Tags"
        headerActions={
          <div
            css={(theme: WeaveTheme) => css`
              align-items: center;
              display: flex;

              & button:not(:last-of-type) {
                margin-right: ${theme.spacing(1)};
              }
            `}
          >
            <PrimaryButton
              onClick={() => {
                setEditable(!editable);
              }}
            >
              {editable ? 'Done Editing' : 'Edit'}
            </PrimaryButton>
          </div>
        }
      >
        <SearchField label="Search tags" name="tagsSearch" {...tagSearchProps} />
        {filteredTags?.map((et: EntityTag) => (
          <WetagsEntity
            tag={getTag(et?.tagId)}
            tagState={et}
            id={locationId}
            et={et}
            globalEdit={editable}
          />
        ))}
      </Page>
    );
  }
};
